import { handleInteractiveKeydown } from '@fullstory/util/a11y';
import { classNames } from '@fullstory/util/fmt';
import React, { PropsWithChildren, ReactNode } from 'react';
import { Flipped, Flipper } from 'react-flip-toolkit';
import { Box } from '../../legacy/Box';
import { identifyComponent } from '../../util';
import { Text } from '../Text';
import css from './index.module.scss';

export type TabListSize = 'small' | 'medium'; // TODO(johnverrone): Add `small` size
export type TabListVariant = 'minimal' | 'underline'; // TODO(johnverrone): Add `outline` variant

export interface TabListProps {
  /** @deprecated, but keeping around to support existing usages of this prop */
  className?: string;
  /** Function called when selecting a tab */
  onSelect: (index: number) => void;
  /** Index of the selected tab */
  selected: number;
  /** Size of the TabList, defaults to `medium` */
  size?: TabListSize;
  /** Tab name elements. If the element is a string, it will render with the Text component. */
  titles: ReactNode[];
  /** Determines certain styles of the tabs */
  variant?: TabListVariant;
  /** Whether to animate the tab indicator */
  animate?: boolean;
}

/**
 * Tabs provide a simple way for users to switch between different views.
 */
export const TabList: React.FC<PropsWithChildren<TabListProps>> = ({
  className,
  onSelect,
  selected,
  size = 'medium',
  titles,
  children,
  variant = 'minimal',
  animate = true,
}) => {
  const content = (
    <Box
      className={classNames(css.tabs, className, {
        [css.underline]: variant === 'underline',
      })}
    >
      {titles.map((title, i) => {
        const isSelected = i === selected;
        return (
          <div
            role="tab"
            aria-selected={isSelected}
            tabIndex={0}
            key={React.isValidElement(title) ? title.key : title?.toString()}
            onClick={() => onSelect(i)}
            onKeyDown={handleInteractiveKeydown(() => onSelect(i))}
            className={classNames(css.tab, {
              [css.tabSmall]: size === 'small',
              [css.selected]: selected === i,
            })}
          >
            {typeof title === 'string' ? (
              <Text appearance="bodyMediumBold" color={isSelected ? 'emphasized' : undefined}>
                {title}
              </Text>
            ) : (
              title
            )}
            {isSelected && (
              <Flipped flipId="tab-indicator">
                <div className={css.tabIndicator} />
              </Flipped>
            )}
          </div>
        );
      })}
    </Box>
  );

  return (
    <>
      <Box data-component="TabList" role="tablist" {...identifyComponent('tab-list')} width={1}>
        {animate ? (
          <Flipper flipKey={selected} spring={{ stiffness: 600, damping: 40 }}>
            {content}
          </Flipper>
        ) : (
          content
        )}
      </Box>
      {React.Children.map(children, (child, i) => {
        if (React.isValidElement(child)) {
          const attrs = {
            isSelected: i === selected,
          };
          return React.cloneElement(child, attrs);
        } else {
          return child;
        }
      })}
    </>
  );
};

interface TabProps {
  isSelected?: boolean;
  className?: string;
}

export const Tab: React.FC<PropsWithChildren<TabProps>> = ({
  isSelected = false,
  className,
  children,
}) => {
  const classes = classNames(className, css.tabPanel, {
    [css.visible]: isSelected,
  });
  return (
    <div data-component="Tab" className={classes} {...identifyComponent('tab-list-tab')}>
      {children}
    </div>
  );
};
